import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { DownloadIcon, SearchIcon, XIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { ScreenHeader } from '@app/components/screen-header'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@app/components/ui/card'
import { Input } from '@app/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumCell,
  TableRow,
  TableScroller,
} from '@app/components/ui/table'
import { cn } from '@app/lib/cn'
import { useCsvDownload } from '@app/lib/download'
import { useUrlSearchInput } from '@app/lib/nav'
import { finansApi, finansQueryKeys } from '@app/features/finans/api'
import type { RateStatus, TahsilatRow } from '@app/features/finans/api'
import { formatAmount, formatDate, formatDateTime } from '@app/features/finans/format'
import { FinansTabs } from '@app/features/finans/tabs'
import { EmptyState, ErrorState, TableSkeleton } from '@app/features/finans/ui'

/**
 * Tahsilat — customer collections straight from the kasa ledger (NT lines on
 * 120.x accounts), each with its real-rate TL equivalent from Korgun's own
 * daily FX table. The advisor's sheet hand-copies this block at flat planning
 * rates; this screen and its two exports are the zero-entry replacement
 * (docs/finans-tahsilat-design.md).
 */

const route = getRouteApi('/_korumali/finans/tahsilat')

/** Non-exact rates get a visible marker; the tooltip carries the rate date. */
const RATE_BADGE: Record<Exclude<RateStatus, 'exact'>, { label: string; tone: 'info' | 'warn' }> = {
  'carried-forward': { label: 'taşınan kur', tone: 'info' },
  missing: { label: 'kur yok', tone: 'warn' },
  'too-old': { label: 'kur eski', tone: 'warn' },
}

const COLUMN = {
  kasa: 'hidden lg:table-cell',
  note: 'hidden xl:table-cell',
  entered: 'hidden 2xl:table-cell',
} as const

export function FinansTahsilatScreen() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const csv = useCsvDownload()

  const from = search.from ?? ''
  const to = search.to ?? ''
  const customer = search.customer ?? ''
  const query = search.query ?? ''

  const [searchInput, setSearchInput, flushSearch] = useUrlSearchInput(query, (text) => {
    void navigate({
      search: (prev) => ({ ...prev, query: text === '' ? undefined : text }),
      replace: true,
      resetScroll: false,
    })
  })

  const params = useMemo(
    () => ({ from: from || undefined, to: to || undefined, customer: customer || undefined, query }),
    [from, to, customer, query],
  )

  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: finansQueryKeys.tahsilat(params),
    queryFn: () => finansApi.tahsilat(params),
    placeholderData: keepPreviousData,
  })

  const rows = data?.rows ?? []
  const dimmed = isFetching && !isPending

  /** A filter write carries text still waiting on the search debounce. */
  function navigateWithPendingText(
    mutate: (prev: Record<string, unknown>) => Record<string, unknown>,
  ) {
    const text = flushSearch({ commit: false })
    void navigate({
      search: (prev) => ({
        ...mutate(prev as Record<string, unknown>),
        query: text === '' ? undefined : text,
      }),
      resetScroll: false,
    })
  }

  /* Danışman export dates: local state seeded from the applied window. */
  const [advisorFrom, setAdvisorFrom] = useState('')
  const [advisorTo, setAdvisorTo] = useState('')
  const appliedFrom = data?.filters.from ?? ''
  const appliedTo = data?.filters.to ?? ''

  return (
    <div className="flex flex-col gap-4">
      <FinansTabs />

      <ScreenHeader
        title="Tahsilat"
        description="Müşteri tahsilatları — kasa fişlerinden, günün gerçek kuruyla; elle giriş yok."
        actions={
          <>
            {csv.error ? (
              <span role="alert" className="text-micro text-hardal">
                {csv.error}
              </span>
            ) : null}
            <Button
              variant="outline"
              disabled={csv.busy}
              aria-busy={csv.busy}
              onClick={() =>
                csv.start('/finans/tahsilat/export', {
                  from: from || undefined,
                  to: to || undefined,
                  customer: customer || undefined,
                  query: flushSearch() || undefined,
                })
              }
            >
              <DownloadIcon aria-hidden />
              {csv.busy ? 'Hazırlanıyor…' : 'Dışa aktar'}
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-micro text-ink-muted">
          Başlangıç
          <Input
            type="date"
            value={from || appliedFrom}
            onChange={(event) =>
              // The API requires from/to as a PAIR — carry the displayed
              // counterpart so editing one side of the default window never
              // sends the half-range the API correctly refuses.
              navigateWithPendingText((prev) => ({
                ...prev,
                from: event.target.value || undefined,
                to: (to || appliedTo) || undefined,
              }))
            }
            className="w-36"
          />
        </label>
        <label className="flex items-center gap-1.5 text-micro text-ink-muted">
          Bitiş
          <Input
            type="date"
            value={to || appliedTo}
            onChange={(event) =>
              navigateWithPendingText((prev) => ({
                ...prev,
                from: (from || appliedFrom) || undefined,
                to: event.target.value || undefined,
              }))
            }
            className="w-36"
          />
        </label>
        {from || to ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              navigateWithPendingText((prev) => ({ ...prev, from: undefined, to: undefined }))
            }
          >
            Son 92 güne dön
          </Button>
        ) : null}
      </div>

      {data && data.byCustomer.length > 0 ? (
        <div className={cn('flex flex-wrap gap-1.5', dimmed && 'opacity-60 transition-opacity')}>
          {data.byCustomer.map((entry) => {
            const active = customer === entry.filterToken
            return (
              <Button
                key={entry.filterToken}
                size="sm"
                variant={active ? 'primary' : 'outline'}
                aria-pressed={active}
                title={
                  entry.tlTotal !== null ? formatAmount(entry.tlTotal, 'TL') : 'TL toplamı eksik'
                }
                onClick={() =>
                  navigateWithPendingText((prev) => ({
                    ...prev,
                    customer: active ? undefined : entry.filterToken,
                  }))
                }
              >
                {entry.label}
                <span className="font-mono text-micro tnum opacity-70">{entry.count}</span>
              </Button>
            )
          })}
        </div>
      ) : null}

      {data && data.byMonth.length > 0 ? (
        <Card className={cn(dimmed && 'opacity-60 transition-opacity')}>
          <CardContent className="flex flex-wrap gap-x-8 gap-y-3">
            {data.byMonth.map((month) => (
              <div key={month.month} className="min-w-32">
                <p className="eyebrow">{formatDate(`${month.month}-01`).slice(3)}</p>
                {month.perCurrency.map((total) => (
                  <p key={total.currency} className="mt-0.5 leading-tight">
                    <span className="font-mono text-data tnum text-ink">
                      {formatAmount(total.amount, total.currency)}
                    </span>{' '}
                    <span className="text-micro text-ink-muted">{total.count}</span>
                  </p>
                ))}
                <p className="mt-0.5 font-mono text-micro tnum text-ink-muted">
                  {month.tlTotal !== null ? `≈ ${formatAmount(month.tlTotal, 'TL')}` : 'kur eksik'}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <div className="relative max-w-xs">
        <SearchIcon
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-ink-muted"
        />
        <Input
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Müşteri, kod, açıklama ara"
          aria-label="Tahsilat ara"
          className="pl-8"
        />
        {searchInput !== '' ? (
          <button
            type="button"
            aria-label="Aramayı temizle"
            className="absolute top-1/2 right-2 -translate-y-1/2 text-ink-muted hover:text-ink"
            onClick={() => setSearchInput('')}
          >
            <XIcon className="size-3.5" aria-hidden />
          </button>
        ) : null}
      </div>

      {data?.truncated ? (
        <p className="text-micro text-hardal">
          Liste {rows.length} satırda kesildi; toplamlar eksik olabilir. Tarih aralığını daraltın.
        </p>
      ) : null}

      <TableScroller className={cn(dimmed && 'opacity-60 transition-opacity')}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Tarih</TableHead>
              <TableHead>Müşteri</TableHead>
              <TableHead className="text-right">Tutar</TableHead>
              <TableHead className="text-right">TL karşılığı</TableHead>
              <TableHead className={COLUMN.kasa}>Kasa</TableHead>
              <TableHead className={COLUMN.note}>Açıklama</TableHead>
              <TableHead className={COLUMN.entered}>Giriş</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isPending ? (
              <TableSkeleton rows={10} cols={7} />
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <ErrorState
                    message={error instanceof Error ? error.message : 'Bilinmeyen hata.'}
                    onRetry={() => void refetch()}
                  />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState
                    title="Tahsilat bulunamadı"
                    description="Seçilen aralıkta ölçüte uyan kasa tahsilatı yok."
                  />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row: TahsilatRow) => (
                <TableRow key={`${row.fisNo}-${row.fisInx}`}>
                  <TableCell className="font-mono tnum whitespace-nowrap">
                    {formatDate(row.date)}
                  </TableCell>
                  <TableCell className="max-w-0">
                    <span className="block truncate text-ink">
                      {row.customerName ?? row.displayKod}
                    </span>
                    <span className="block truncate font-mono text-micro tnum text-ink-muted">
                      {row.displayKod}
                    </span>
                  </TableCell>
                  <TableNumCell className="whitespace-nowrap font-medium">
                    {formatAmount(row.amount, row.currency)}
                  </TableNumCell>
                  <TableNumCell className="whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      {row.tlValue !== null ? (
                        <span className="font-mono tnum">{formatAmount(row.tlValue, 'TL')}</span>
                      ) : (
                        <span className="text-ink-muted/60">—</span>
                      )}
                      {row.rateStatus !== 'exact' ? (
                        <Badge
                          tone={RATE_BADGE[row.rateStatus].tone}
                          title={
                            row.rateDate !== null
                              ? `Kur tarihi: ${formatDate(row.rateDate)}`
                              : 'Kur bulunamadı'
                          }
                        >
                          {RATE_BADGE[row.rateStatus].label}
                        </Badge>
                      ) : null}
                    </span>
                  </TableNumCell>
                  <TableCell className={cn(COLUMN.kasa, 'font-mono text-micro tnum text-ink-muted')}>
                    {row.kasa ?? '—'}
                  </TableCell>
                  <TableCell className={cn(COLUMN.note, 'max-w-0')}>
                    <span className="block truncate text-ink-muted">{row.note ?? '—'}</span>
                  </TableCell>
                  <TableCell className={cn(COLUMN.entered, 'whitespace-nowrap')}>
                    <span className="font-mono text-micro tnum text-ink-muted">
                      {formatDateTime(row.enteredAt)}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableScroller>

      <Card>
        <CardHeader>
          <CardTitle>Danışman aktarımı</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-data text-ink-muted">
            Nakit akış dosyasının biçiminde, dört Korgun bloğunu (tahsilatlar, müşteri çekleri,
            tedarikçi ödemeleri, firma çekleri — banka kaydına göre GARANTİ 2.6.1.1 ve HALKBANK
            2.6.1.2 satırları olarak) içeren CSV üretir.{' '}
            <span className="text-ink">
              Dosyanızda seçilen aralığa düşen bu blok satırlarını silin, yerine yapıştırın; sonra
              C, D, E, O ve P sütunlarını üstteki satırdan aşağı doldurun
            </span>{' '}
            — O ve P danışmanın kendi formülleridir. İleri vadeli satırlarda kur, bilinen son
            kurdur. Takma adı tanımlı olmayan müşteriler ülke bilgisine göre yurt içi / yurt dışı
            bloğuna ayrılır — en iyi tahmindir, kesin sınıflandırma değildir.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex items-center gap-1.5 text-micro text-ink-muted">
              Başlangıç
              <Input
                type="date"
                value={advisorFrom || appliedFrom}
                onChange={(event) => setAdvisorFrom(event.target.value)}
                className="w-36"
              />
            </label>
            <label className="flex items-center gap-1.5 text-micro text-ink-muted">
              Bitiş
              <Input
                type="date"
                value={advisorTo || appliedTo}
                onChange={(event) => setAdvisorTo(event.target.value)}
                className="w-36"
              />
            </label>
            <Button
              variant="primary"
              disabled={csv.busy}
              aria-busy={csv.busy}
              onClick={() =>
                csv.start('/finans/danisman/export', {
                  from: (advisorFrom || appliedFrom) || undefined,
                  to: (advisorTo || appliedTo) || undefined,
                })
              }
            >
              <DownloadIcon aria-hidden />
              Danışman CSV indir
            </Button>
          </div>
        </CardContent>
      </Card>

      <p className="mt-2 text-micro text-ink-muted">
        Kaynak: Korgun kasa fişleri (NT, müşteri hesapları) ve günlük kur tablosu. Tutarlar para
        birimi bazında ayrı izlenir; TL karşılığı fiş tarihindeki gerçek kurla hesaplanır, kur
        yoksa toplam gösterilmez. Geç girilen fişler kapanmış ay toplamlarını sonradan
        değiştirebilir. Tedarikçi ödemelerinin satır dökümü için cari ekstre ekranı kullanılır.
      </p>
    </div>
  )
}
